import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { PageBanner } from "./Attorneys";
import { CONSTRUCTION_IMAGE, FOUNDERS_IMAGE } from "../mock";

const values = [
  {
    title: "Perspective",
    body: "Together, our attorneys have represented plaintiffs and defendants, contractors and owners, policyholders and insurers, product manufacturers and designers. Our range provides us with perspective which allows us to think creatively to find solutions.",
  },
  {
    title: "Preparation",
    body: "Cases are won on the details. We prepare every matter as though it is going to trial — because sometimes it does.",
  },
  {
    title: "Judgment",
    body: "Clients hire lawyers to make judgment calls, provide options and implement plans of action. Our advice is candid, specific, and structured to your goals.",
  },
  {
    title: "Responsiveness",
    body: "Our size is our feature. You will work directly with our partners, not through gatekeepers.",
  },
];

const About = () => {
  return (
    <div className="bg-white">
      <PageBanner
        eyebrow="About the Firm"
        title="Local Roots. Statewide Reach."
        subtitle="Newberry Wieters is a Charleston litigation boutique founded on the idea that clients deserve senior-level attention on every matter."
      />

      {/* Intro */}
      <section className="py-20 md:py-24">
        <div className="container-nw grid md:grid-cols-12 gap-12 items-start">
          <div className="md:col-span-5">
            <div className="text-nw-gold text-xs uppercase tracking-[0.3em] mb-4">
              Our Story
            </div>
            <h2 className="font-serif-display text-4xl md:text-5xl text-nw-navy leading-tight">
              Built for complex matters —{" "}
              <span className="italic">without the layers.</span>
            </h2>
            <div className="h-0.5 w-16 bg-nw-gold mt-6" />
          </div>
          <div className="md:col-span-7 space-y-5 text-nw-navy/80 text-base md:text-lg leading-relaxed">
            <p>
              Newberry Wieters was founded by two Charleston, SC based
              attorneys who spent much of their careers with a large national
              firm handling multi-million-dollar construction, coverage,
              injury, and commercial disputes across South Carolina. They
              founded Newberry Wieters to continue providing large firm
              experience, but in a small firm atmosphere where clients
              receive senior-level, personalized attention with a local focus.
            </p>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-20 md:py-24">
        <div className="container-nw">
          <div className="max-w-2xl mb-14">
            <div className="text-nw-gold text-xs uppercase tracking-[0.3em] mb-4">
              How We Practice
            </div>
            <h2 className="font-serif-display text-4xl md:text-5xl text-nw-navy">
              Four things we care about.
            </h2>
            <div className="h-0.5 w-16 bg-nw-gold mt-6" />
          </div>
          <div className="grid md:grid-cols-2 gap-6 lg:gap-8">
            {values.map((v, i) => (
              <div
                key={v.title}
                className="card-lift border border-nw-stone p-8 lg:p-10 bg-white relative"
              >
                <div className="flex items-start gap-4">
                  <div className="text-nw-gold font-serif-display text-3xl leading-none">
                    0{i + 1}
                  </div>
                  <div>
                    <h3 className="font-serif-display text-2xl text-nw-navy">
                      {v.title}
                    </h3>
                    <div className="h-0.5 w-10 bg-nw-gold mt-3 mb-4" />
                    <p className="text-nw-navy/70 leading-relaxed">{v.body}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Feature strip */}
      <section className="py-20 md:py-24 bg-nw-cream">
        <div className="container-nw grid md:grid-cols-2 gap-12 items-center">
          <div className="relative aspect-[4/3] overflow-hidden order-2 md:order-1">
            <img
              src={FOUNDERS_IMAGE}
              alt="Derek Newberry and Elizabeth Wieters"
              className="w-full h-full object-cover"
              style={{ objectPosition: "50% 28%" }}
            />
            <div className="absolute -bottom-4 -right-4 hidden md:block w-32 h-32 border-2 border-nw-gold" />
          </div>
          <div className="order-1 md:order-2">
            <div className="text-nw-gold text-xs uppercase tracking-[0.3em] mb-4">
              A Different Kind of Practice
            </div>
            <h2 className="font-serif-display text-4xl md:text-5xl text-nw-navy leading-tight">
              Senior attention on{" "}
              <span className="italic">every</span> matter.
            </h2>
            <div className="h-0.5 w-16 bg-nw-gold mt-6 mb-6" />
            <p className="text-nw-navy/80 leading-relaxed mb-6">
              We deliberately kept the firm small. When you retain Newberry
              Wieters, you get a founding partner who knows the file, has
              tried cases in this state, and will pick up the phone.
            </p>
            <Link
              to="/contact"
              className="group inline-flex items-center gap-2 mt-4 bg-nw-navy hover:bg-nw-navy-light text-white px-7 py-4 text-xs font-bold uppercase tracking-[0.15em] transition-all"
            >
              Talk to a Partner <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>
        </div>
      </section>

      {/* Second image band */}
      <section className="py-20 md:py-24">
        <div className="container-nw grid md:grid-cols-2 gap-12 items-center">
          <div>
            <div className="text-nw-gold text-xs uppercase tracking-[0.3em] mb-4">
              Complex Disputes
            </div>
            <h2 className="font-serif-display text-4xl md:text-5xl text-nw-navy leading-tight">
              Construction, coverage, and everything they touch.
            </h2>
            <div className="h-0.5 w-16 bg-nw-gold mt-6 mb-6" />
            <Link
              to="/practice-areas"
              className="group inline-flex items-center gap-2 mt-2 text-nw-navy hover:text-nw-gold text-xs font-bold uppercase tracking-[0.15em] link-underline"
            >
              Explore Practice Areas <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="relative aspect-[4/3] overflow-hidden">
            <img
              src={CONSTRUCTION_IMAGE}
              alt="Construction litigation"
              className="w-full h-full object-cover"
            />
            <div className="absolute -top-4 -left-4 hidden md:block w-32 h-32 border-2 border-nw-gold" />
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
